import React, {useEffect, useRef, useState} from "react";
import {
    Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogTitle, IconButton, Table, TableBody, TableCell, TableContainer, TableRow
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const VALIDATION_STATUS = {
    SUCCESS: {label: "Validation passed", color: "success"},
    FAILED: {label: "Validation failed", color: "error"},
    NONE: {label: "Validation not run", color: "default"},
};

const externalUrl = value => {
    try {
        const url = new URL(value);
        return ["https:", "http:"].includes(url.protocol) ? url.href : null;
    } catch {
        return null;
    }
};

function ConfigBuildButton({url, children}) {
    const href = externalUrl(url);
    return <Button variant="outlined" size="small" href={href || undefined} disabled={!href}
                   target="_blank" rel="noopener noreferrer">{children}</Button>;
}

export default function CampaignConfigDialog({campaign, open, setOpen}) {
    const [jsonOpen, setJsonOpen] = useState(false);
    const [json, setJson] = useState("");
    const [loading, setLoading] = useState(null);
    const [error, setError] = useState(null);
    const request = useRef(null);
    const configUrl = externalUrl(campaign.absoluteConfigUrl);

    useEffect(() => {
        setJsonOpen(false);
        setJson("");
        setError(null);
        setLoading(null);
        return () => {
            request.current?.abort();
            request.current = null;
        };
    }, [configUrl, open]);

    const close = () => {
        request.current?.abort();
        setOpen(false);
    };

    const closeJson = () => {
        request.current?.abort();
        setJsonOpen(false);
    };

    const loadConfig = async action => {
        if (!configUrl || loading) return;
        const controller = new AbortController();
        request.current = controller;
        setError(null);
        setLoading(action);
        if (action === "open") {
            setJson("");
            setJsonOpen(true);
        }
        try {
            const response = await fetch(configUrl, {signal: controller.signal, credentials: "omit"});
            if (!response.ok) throw new Error(`Could not load config (HTTP ${response.status}).`);
            const blob = await response.blob();
            if (controller.signal.aborted) return;
            if (action === "open") {
                const text = await blob.text();
                if (controller.signal.aborted) return;
                try {
                    setJson(JSON.stringify(JSON.parse(text), null, 2));
                } catch {
                    throw new Error("The config file does not contain valid JSON.");
                }
            } else {
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;
                link.download = campaign.config.fileName || "config.json";
                document.body.appendChild(link);
                link.click();
                link.remove();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
            }
        } catch (cause) {
            if (!controller.signal.aborted) {
                setError(cause instanceof TypeError
                    ? "Could not load config from CDN. Check the connection and try again."
                    : cause.message);
            }
        } finally {
            if (request.current === controller) {
                request.current = null;
                setLoading(null);
            }
        }
    };

    if (!campaign.config) return null;

    const config = campaign.config;
    const status = VALIDATION_STATUS[config.validationStatus];
    const rows = [
        ["Config file in CDN", <Box sx={{display: "flex", gap: 1, flexWrap: "wrap"}}>
            <Button variant="outlined" size="small" disabled={!configUrl || !!loading}
                    onClick={() => loadConfig("open")}>Open</Button>
            <Button variant="outlined" size="small" disabled={!configUrl || !!loading}
                    onClick={() => loadConfig("download")}>
                {loading === "download" ? "Downloading…" : "Download"}
            </Button>
        </Box>],
        ["Validation", status
            ? <Chip size="small" label={status.label} color={status.color} />
            : config.validationStatus || "—"],
        ["Config build link", <ConfigBuildButton url={config.buildUrl}>Link</ConfigBuildButton>],
    ];

    return (
        <>
            <Dialog open={open} onClose={close} fullWidth maxWidth="md"
                    aria-labelledby="campaign-config-title">
                <DialogTitle id="campaign-config-title" sx={{pr: 6, overflowWrap: "anywhere"}}>
                    Config: {campaign.id}
                </DialogTitle>
                <IconButton aria-label="Close config" onClick={close}
                            sx={{position: "absolute", right: 8, top: 8, color: theme => theme.palette.grey[500]}}>
                    <CloseIcon />
                </IconButton>
                <DialogContent>
                    {error && !jsonOpen && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
                    <TableContainer>
                        <Table aria-label="Campaign config details" size="small" sx={{tableLayout: "fixed"}}>
                            <TableBody>
                                {rows.map(([label, value]) => (
                                    <TableRow key={label}>
                                        <TableCell component="th" scope="row" sx={{width: "30%", verticalAlign: "middle"}}>
                                            {label}
                                        </TableCell>
                                        <TableCell sx={{overflowWrap: "anywhere"}}>{value}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>
                    <Button variant="contained" onClick={close}>Close</Button>
                </DialogActions>
            </Dialog>
            <Dialog open={open && jsonOpen} onClose={closeJson} fullWidth maxWidth="lg"
                    aria-labelledby="campaign-config-json-title">
                <DialogTitle id="campaign-config-json-title" sx={{pr: 6, overflowWrap: "anywhere"}}>
                    Config JSON: {campaign.id}
                </DialogTitle>
                <IconButton aria-label="Close config JSON" onClick={closeJson}
                            sx={{position: "absolute", right: 8, top: 8, color: theme => theme.palette.grey[500]}}>
                    <CloseIcon />
                </IconButton>
                <DialogContent>
                    {loading === "open" && <Box sx={{display: "flex", justifyContent: "center", p: 3}}>
                        <CircularProgress aria-label="Loading config JSON" />
                    </Box>}
                    {error && <Alert severity="error">{error}</Alert>}
                    {!loading && !error && <Box component="pre" sx={{
                        m: 0, p: 2, borderRadius: 1, bgcolor: "grey.100", fontFamily: "monospace",
                        fontSize: 14, lineHeight: 1.6, overflow: "auto", maxHeight: "65vh",
                        whiteSpace: "pre-wrap", overflowWrap: "anywhere"
                    }}>{json}</Box>}
                </DialogContent>
                <DialogActions>
                    {error && <Button onClick={() => loadConfig("open")} disabled={!!loading}>Retry</Button>}
                    <Button variant="contained" onClick={closeJson}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
